<template>
    <Modal close-esc-event="keydown" size="5xl" :close-button="false" modal-class="m-auto w-full border-0" mask-class="grid" @closing="onModalClose">
        <template #toggle="scope">
            <button type="button" class="bg-primary-700 text-primary-800 bg-opacity-20 hover:bg-opacity-30 transition-colors rounded-lg py-2 px-3 flex" @click="onModalOpen(scope.open)">
                <span class="m-auto pr-2">
                    <Icon :src="IconSource.DatePicker"></Icon>
                </span>
                <span class="font-medium">{{ $t('views.activityTable.calendarButtonLabel') }}</span>
            </button>
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body>
            <div class="bg-white divide-x flex rounded-xl w-full h-120">
                <div class="py-8 px-4 flex-1">
                    <FullCalendarWrapper v-model:date-string="date" :events="calendarEvents" @dates-set="onDatesSet"></FullCalendarWrapper>
                </div>
                <div class="flex-1 relative">
                    <Transition
                        enter-active-class="duration-200 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <div v-if="isRecordsLoading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                            <div class="flex justify-center items-center gap-2">
                                <div class="font-medium">{{ $t('global.uiElements.dataTable.texts.loading') }}</div>
                                <Spinner class="w-5 h-5 animate-spin" />
                            </div>
                        </div>
                    </Transition>
                    <TableCalendarModalEvents v-model:events="filteredEvents"></TableCalendarModalEvents>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { startOfDay, endOfDay, parseISO, isSameDay, getYear, getMonth, getDate, format } from 'date-fns';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { IconSource } from '@/types';
import FullCalendarWrapper from '@/components/ui/FullCalendarWrapper.vue';
import TableCalendarModalEvents from '@/components/common/TableCalendarModalEvents.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import { useRecords, useRecordTypes, type RecordsFetchParams } from '@/plugins/store/actions/queries/records';
import { store } from '@/plugins/store';
import Spinner from '@/components/ui/Spinner.vue';
import { useRecordsSearchParams } from '@/composables/records/useRecordsSearchParams';

const props = withDefaults(
    defineProps<{
        selectableRecordTypeIds?: number[];
    }>(),
    {
        selectableRecordTypeIds: () => [],
    }
);

const date = ref('');
const isModalOpen = ref(false);

const calendarPeriod = ref<{ start: Date; end: Date } | null>(null);

const { fetchParams: tableFetchParams } = useRecordsSearchParams();
const { data: recordTypes } = useRecordTypes();

const selectedTypeIds = computed(() => tableFetchParams.value.typeIds || []);

const filteredRecordTypes = computed(() => {
    const selectable = recordTypes.value?.filter((t) => props.selectableRecordTypeIds.includes(t.id)) || [];

    if (selectedTypeIds.value.length === 0) {
        return selectable;
    }
    return selectable.filter((t) => selectedTypeIds.value.includes(t.id));
});

const calendarPeriodString = computed(() => {
    if (!calendarPeriod.value) {
        return '';
    }
    const startDate = startOfDay(calendarPeriod.value.start);
    const endDate = endOfDay(calendarPeriod.value.end);

    return `from=${startDate.toISOString()}&to=${endDate.toISOString()}&fromDateOnly=${format(startDate, 'yyyy-MM-dd')}&toDateOnly=${format(endDate, 'yyyy-MM-dd')}`;
});

const fetchParams = computed<RecordsFetchParams>(() => ({
    page: 1,
    pageSize: 5000,
    typeIds: selectedTypeIds.value.length > 0 ? selectedTypeIds.value : props.selectableRecordTypeIds,
    period: calendarPeriodString.value,
    siteIds: store.getters.getSelectedSiteIds,
}));

const isQueryEnabled = computed(() => isModalOpen.value && calendarPeriod.value !== null && props.selectableRecordTypeIds.length > 0 && fetchParams.value.siteIds.length > 0);
const { data: recordsResponse, isFetching: isRecordsLoading } = useRecords(fetchParams, { enabled: isQueryEnabled });

const records = computed(
    () =>
        recordsResponse.value?.data.map((r) => {
            const recordType = filteredRecordTypes.value.find((t) => t.id === r.type_id);
            return {
                ...r,
                name: recordType?.name,
                icon_url: recordType?.icon_url,
            };
        }) || []
);

const calendarEvents = computed(() => {
    const colors = filteredRecordTypes.value.reduce((accum, current) => {
        accum[current.id] = current.cal_color_hex;
        return accum;
    }, {} as { [key: string]: string });

    return Object.values(
        records.value.reduce((accum, current) => {
            const currentDate = parseISO(current.date_only || current.date);
            const uniqueKey = `${current.type_id}${getYear(currentDate)}${getMonth(currentDate)}${getDate(currentDate)}`;

            accum[uniqueKey] = {
                start: currentDate,
                allDay: true,
                color: colors[current.type_id] || '#000000',
            };

            return accum;
        }, {} as { [key: string]: { start: Date; allDay: boolean; color: string } })
    );
});

const filteredEvents = computed(() => {
    if (!date.value) {
        return [];
    }

    const selectedDate = parseISO(date.value);

    return records.value.filter((event) => {
        const eventDate = parseISO(event.date_only || event.date);
        return isSameDay(selectedDate, eventDate);
    });
});

function onDatesSet(info: { start: Date; end: Date }) {
    date.value = '';

    const newStart = startOfDay(info.start);
    const newEnd = endOfDay(info.end);

    if (!calendarPeriod.value) {
        calendarPeriod.value = {
            start: newStart,
            end: newEnd,
        };
        return;
    }

    const currentStart = startOfDay(calendarPeriod.value.start);
    const currentEnd = endOfDay(calendarPeriod.value.end);

    if (newStart.getTime() !== currentStart.getTime() || newEnd.getTime() !== currentEnd.getTime()) {
        calendarPeriod.value = {
            start: newStart,
            end: newEnd,
        };
    }
}

function onModalOpen(open: CallableFunction) {
    isModalOpen.value = true;
    open();
}

function onModalClose() {
    isModalOpen.value = false;
}
</script>
